import {retrieveToggles, isEnabled, retrieveTogglesBasedOnEnvironment} from '@nuskin/ns-feature-flags';

export const OVERRIDE_USER_PARAM = '$featureFlagUser';
export const OVERRIDE_FLAG_ENABLE_PARAM = '$enableFeatureFlags';
export const OVERRIDE_FLAG_DISABLE_PARAM = '$disableFeatureFlags';

let silenceDeprecationWarning = false;

export default {
    /** istanbul ignore next */
    set silenceDeprecationWarning(value) {
        silenceDeprecationWarning = value;
    },
    install(Vue, {url, user} = {}) {
        /** istanbul ignore next */
        if (!silenceDeprecationWarning) {
            console.warn(
                '[ns-feature-flags-vue] - Use of the plugin-based interface is deprecated.',
                'See the ns-feature-flags-vue README for more information.'
            );
        }
        const vm = createFeaturesVm(Vue, url, user);

        Vue.mixin({
            beforeCreate() {
                Object.defineProperty(this, '$features', {
                    get: () => vm.features 
                });
            }
        });
    }
}

/**
 * Constructs a Vue instance to store and manage feature flags.
 * By putting the flags on the Vue instance, we get reactivity -
 * every time we change the value of 'features' or 'user', Vue
 * will detect that change and trigger a render.
 * 
 * @param {*} Vue Vue constructor
 * @param {string} url URL to use for API calls
 * @param {string} [user] user override
 */
function createFeaturesVm(Vue, url, user) {
    const _vm = new Vue({
        data: () => ({
            features: null,
            featureFlagUser: user
        })
    });

    const proxy = createProxy(new FeatureFlagBase(url, _vm));

    _vm.features = proxy;

    // Trigger the initial flags load without waiting for the promise.
    proxy.$forceReload();

    return _vm;
}

// We use Symbols to make it easy to see our own values without exposing our internals to the world.
//  Editorial: Private fields are gonna be so very nice once they ship everywhere.
const _url = Symbol('url'); 
const _vm = Symbol('vm');
const _currentPromise = Symbol('currentPromise');
const _overrides = Symbol('overrides');

/**
 * Feature Flag utility APIs. Allows consumers to control the loading of flags.
 * This class is always wrapped in a proxy which forwards all unknown properties to
 * the feature flag service.
 */
class FeatureFlagBase {
    constructor(
        url, vm
    ) {
        this[_url] = url;
        this[_vm] = vm;
        // currentPromise is used to synchronize work across all functions.
        // We'll start it off with an empty promise.
        this[_currentPromise] = Promise.resolve();
        this[_overrides] = new Map();
    }

    /**
     * Returns a promise which resolves when the current flags load finishes.
     */
    async $waitForLoad() {
        await this[_currentPromise];
    }

    /**
     * Triggers a flag reload and returns a promise which resolves when the load
     * is finished. 
     */
    async $forceReload() {
        const url = this[_url];
        const vm = this[_vm];
        // Get the current overrides
        this[_overrides] = getFlagOverrides();
        // Trigger the reloading of the flags...
        this[_currentPromise] = fetchFlags(url, vm)
            // ...then set the value on the Vue instance to notify our consumers
            .then(() => vm.features = createProxy(this))
            // ...and warn on any errors instead of crashing
            .catch(err => {
                console.warn(`Error loading feature flags from [${url}]!`, err);
            });
        await this.$waitForLoad();
    }

    /**
     * Overrides the user to be sent to the feature flag server.
     * If this is set to a truthy value, the value will be used instead
     * of any other value.
     * 
     * This function triggers a reload and returns a promise that resolves
     * when the reload finishes.
     * 
     * @param {string?} user user ID for feature flag server
     */
    async $setUser(user) {
        this[_vm].featureFlagUser = user;
        return this.$forceReload();
    }
}

// Wraps a FeatureFlagBase in a proxy
function createProxy(base) {
    const handler = {
        get(target, prop) {
            // Forward some things onto the target
            if (prop in target) {
                return target[prop];
            }
            const overrides = target[_overrides];
            if (overrides.has(prop)) {
                return overrides.get(prop);
            }
            // Anything we don't recognize is a flag name.
            return isEnabled(prop);
        }
    };
    return new Proxy(base, handler);
}

// Actually triggers a flag reload
async function fetchFlags(url, vm) {
    const user = getUserOverride(vm);
    if (url) {
        return retrieveToggles(url, user)
    } else {
        return retrieveTogglesBasedOnEnvironment({
            userId: user
        })
    }
}

/**
 * Determines what user to send to the feature flag user.
 * 
 * If a user was set when adding the plugin to Vue or by a call
 * to $features.$setUser, it will be used.
 * 
 * Otherwise, if we are in a browser environment, 
 * we look for a query parameter named $featureFlagUser.
 * 
 * @param {*} vm Vue instance 
 */
function getUserOverride(vm) {
    if (vm && vm.featureFlagUser) {
        return vm.featureFlagUser;
    }
    const query = getQueryParams();
    if (query.has(OVERRIDE_USER_PARAM)) {
        return query.get(OVERRIDE_USER_PARAM);
    }
    return null;
}

function getQueryParams() {
    if (window && window.location && window.location.search) {
        return new URLSearchParams(window.location.search);
    }
    return new URLSearchParams('');
}

function getFlagOverrides() {
    const disabledPairs = getOverrideList(OVERRIDE_FLAG_DISABLE_PARAM)
        .map(it => [it, false]);
    const enabledPairs = getOverrideList(OVERRIDE_FLAG_ENABLE_PARAM)
        .map(it => [it, true]);

    return new Map([...disabledPairs, ...enabledPairs]);
}

function getOverrideList(paramName) {
    const query = getQueryOverrides(paramName);
    const session = getSessionOverrides(paramName);

    return [...query, ...session];
}

function getQueryOverrides(paramName) {
    const query = getQueryParams();
    return query.getAll(paramName)
        // flatMap but without the flatMap
        .reduce((a, each) => a.concat(each.split(',')), []);
}

function getSessionOverrides(paramName) {
    const found = sessionStorage.getItem(paramName);
    if (found) {
        return found.split(',');
    } else {
        return [];
    }
}
